import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
    Plus, Pencil, Search, X, Loader2, Star, Ban, Store as StoreIcon,
    Snowflake, Lock, ShoppingBag, Boxes, LayoutGrid, MapPin, Pill,
    AlertTriangle, ArrowDownUp, Trash2, PackagePlus,
} from 'lucide-react';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import CommonLoader from '../../components/CommonLoader';
// A factory, not a hook — imported under its real name so
// react-hooks/rules-of-hooks does not key on the identifier. See usePermissions.js.
import createAuthedAxios from '../../hooks/useAxiosAuth';
import usePermissions from '../../hooks/usePermissions';

const MySwal = withReactContent(Swal);

// THE PHARMACY AS A ROOM: which counters exist, and what is on which shelf.
//
// WHY A MAP RATHER THAN A LIST
// A bin code is only useful if somebody can find the bin, and a table sorted
// alphabetically bears no relation to the walk a pharmacist actually does. The
// grid below is grouped by rack and ordered by pickSequence for that reason —
// it is the picking route, drawn. A store that never sets pickSequence still
// gets a stable order (the server breaks ties on code) rather than a different
// one on every request.
//
// WHY COLOUR IS DOING COMPLIANCE WORK HERE
// Cold chain and the narcotic cupboard are not two more categories in a
// dropdown. A vaccine that spent a night at room temperature looks exactly like
// one that did not, and Schedule X stock outside a double-locked cupboard with
// a bound register is an offence rather than an untidiness. Both failures are
// invisible on the shelf, so they are made loud on the screen: a distinct
// colour, an icon, and a standing note on the bin itself. Everything else is
// deliberately muted so those two keep their signal.
//
// TWO DIFFERENT PERMISSIONS LIVE ON THIS SCREEN, which is why the buttons are
// enabled independently. `pharmacy.location.manage` shapes the room — creating,
// editing and retiring bins. `pharmacy.stock.manage` decides what lives in one,
// because assigning a medicine a home bin is an edit to the drug master and the
// server gates it there. A rack planner who is not a stock controller can lay
// out the shelves and not re-home anybody's medicine.
//
// NO REDUX SLICE — see the note in MedicineMaster.jsx. Registering one requires
// touching src/store.js, which this screen ships ahead of.

const apiError = (err, fallback) =>
    err?.response?.data?.message || err?.message || fallback;

const SWEEP_LIMIT = 200;

// Mirrors LOCATION_TYPES in Backend/api/controllers/pharmacyMasterController.js.
// Order is the order of the picker: the two that carry a legal obligation are
// not buried at the bottom of the list.
const BIN_TYPES = {
    shelf: {
        labelKey: 'rackpage.type_shelf',
        label: 'Shelf',
        icon: LayoutGrid,
        card: 'bg-white border-slate-200 hover:border-[#4ABDBD]',
        chip: 'bg-[#4ABDBD]/10 text-[#2D8A8A] border-[#4ABDBD]/40',
        note: null,
    },
    cold_chain: {
        labelKey: 'rackpage.type_cold_chain',
        label: 'Cold chain',
        icon: Snowflake,
        card: 'bg-sky-50/70 border-sky-300 border-dashed hover:border-sky-500',
        chip: 'bg-sky-100 text-sky-800 border-sky-300',
        noteKey: 'rackpage.note_cold_chain',
        note: '2–8 °C, temperature-logged. Stock that broke the chain looks identical to stock that did not.',
    },
    narcotic_cupboard: {
        labelKey: 'rackpage.type_narcotic_cupboard',
        label: 'Narcotic cupboard',
        icon: Lock,
        card: 'bg-rose-50/70 border-rose-400 ring-1 ring-rose-200 hover:border-rose-600',
        chip: 'bg-rose-100 text-rose-800 border-rose-400',
        noteKey: 'rackpage.note_narcotic',
        note: 'Double-locked with a bound register. Schedule X belongs here and nowhere else.',
    },
    otc_front: {
        labelKey: 'rackpage.type_otc_front',
        label: 'OTC front',
        icon: ShoppingBag,
        card: 'bg-[#D7A05C]/10 border-[#D7A05C]/50 hover:border-[#D7A05C]',
        chip: 'bg-[#D7A05C]/15 text-[#8a6127] border-[#D7A05C]/50',
        noteKey: 'rackpage.note_otc',
        note: 'Front of counter, reachable by the public. Nothing needing a prescription belongs here.',
    },
    bulk: {
        labelKey: 'rackpage.type_bulk',
        label: 'Bulk store',
        icon: Boxes,
        card: 'bg-slate-50 border-slate-300 hover:border-slate-400',
        chip: 'bg-slate-200 text-slate-700 border-slate-300',
        noteKey: 'rackpage.note_bulk',
        note: 'Back stock, not a pick face. Move it forward before it is dispensed from.',
    },
};
const BIN_TYPE_KEYS = Object.keys(BIN_TYPES);

const STORE_TYPES = [
    { value: 'op', labelKey: 'rackpage.store_op', label: 'Outpatient counter' },
    { value: 'ip', labelKey: 'rackpage.store_ip', label: 'Inpatient / ward pharmacy' },
    { value: 'main', labelKey: 'rackpage.store_main', label: 'Main store' },
];

const EMPTY_STORE = { storeId: null, name: '', code: '', type: 'op', address: '', gstin: '', drugLicenseNo: '', isDefault: false };
const EMPTY_BIN = { locationId: null, code: '', rackName: '', rowLabel: '', colLabel: '', bin: '', type: 'shelf', pickSequence: 0 };

const fmtQty = (n) => (Number.isFinite(Number(n)) ? String(Math.round(Number(n) * 100) / 100) : '0');
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB') : '—');

export default function RackLocations() {
    const { t } = useTranslation();
    const { can } = usePermissions();
    const axiosInstance = useMemo(() => createAuthedAxios(), []);

    const canManageLocations = can('pharmacy.location.manage');
    const canManageStock = can('pharmacy.stock.manage');

    const [stores, setStores] = useState([]);
    const [storesLoading, setStoresLoading] = useState(true);
    const [storeId, setStoreId] = useState('');

    const [racks, setRacks] = useState([]);
    const [totalBins, setTotalBins] = useState(0);
    const [mapLoading, setMapLoading] = useState(false);

    const [storeDraft, setStoreDraft] = useState(null);
    const [binDraft, setBinDraft] = useState(null);
    const [mutating, setMutating] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);

    // The bin whose contents are open below the map. Held by id rather than by
    // object so it survives a refresh of the map without going stale.
    const [selectedBinId, setSelectedBinId] = useState(null);
    const [binMedicines, setBinMedicines] = useState([]);
    const [binMedicinesLoading, setBinMedicinesLoading] = useState(false);

    const [assignQuery, setAssignQuery] = useState('');
    const [assignResults, setAssignResults] = useState([]);
    const [assignLoading, setAssignLoading] = useState(false);
    const assignTimeoutRef = useRef(null);

    const refetch = useCallback(() => setRefreshKey((k) => k + 1), []);

    // ---- stores -----------------------------------------------------------

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setStoresLoading(true);
            try {
                const res = await axiosInstance.get('/pharmacy/stores');
                if (cancelled) return;
                const rows = res?.data?.data || [];
                setStores(rows);
                setStoreId((current) => {
                    if (current && rows.some((s) => s.storeId === current)) return current;
                    const active = rows.filter((s) => s.status === 'active');
                    return active.find((s) => s.isDefault)?.storeId || active[0]?.storeId || '';
                });
            } catch (err) {
                if (!cancelled) toast.warn(apiError(err, t('rackpage.stores_error', 'Could not load the pharmacy counters')));
            } finally {
                if (!cancelled) setStoresLoading(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshKey]);

    // ---- the rack map -----------------------------------------------------

    useEffect(() => {
        if (!storeId) { setRacks([]); setTotalBins(0); return undefined; }
        let cancelled = false;
        (async () => {
            setMapLoading(true);
            try {
                const res = await axiosInstance.get('/pharmacy/locations/map', { params: { storeId } });
                if (cancelled) return;
                setRacks(res?.data?.data?.racks || []);
                setTotalBins(res?.data?.data?.totalBins || 0);
            } catch (err) {
                if (cancelled) return;
                setRacks([]);
                setTotalBins(0);
                toast.warn(apiError(err, t('rackpage.map_error', 'Could not load the rack map')));
            } finally {
                if (!cancelled) setMapLoading(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storeId, refreshKey]);

    const allBins = useMemo(() => racks.flatMap((r) => r.bins || []), [racks]);
    const selectedBin = useMemo(
        () => allBins.find((b) => b.locationId === selectedBinId) || null,
        [allBins, selectedBinId]
    );

    // Changing counter must not leave a bin from the previous one open below the
    // map — the panel would keep answering about a shelf in another room.
    useEffect(() => { setSelectedBinId(null); }, [storeId]);

    // ---- what lives in the open bin ---------------------------------------

    useEffect(() => {
        if (!selectedBinId) { setBinMedicines([]); return undefined; }
        let cancelled = false;
        (async () => {
            setBinMedicinesLoading(true);
            try {
                const res = await axiosInstance.get('/pharmacy/medicines', {
                    params: { locationId: selectedBinId, isActive: 'true', limit: SWEEP_LIMIT },
                });
                if (!cancelled) setBinMedicines(res?.data?.data || []);
            } catch (err) {
                if (!cancelled) {
                    setBinMedicines([]);
                    toast.warn(apiError(err, t('rackpage.bin_medicines_error', 'Could not load what is in this bin')));
                }
            } finally {
                if (!cancelled) setBinMedicinesLoading(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedBinId, refreshKey]);

    // Type-ahead over the whole catalogue, debounced like every other search on
    // the platform. Deliberately NOT /medicines/search: that endpoint answers a
    // counter's question (stock, batch, price at one store) and this one is
    // asking a filing question about the catalogue.
    useEffect(() => {
        if (assignTimeoutRef.current) clearTimeout(assignTimeoutRef.current);
        const q = assignQuery.trim();
        if (!selectedBinId || q.length < 2) { setAssignResults([]); return undefined; }
        assignTimeoutRef.current = setTimeout(async () => {
            setAssignLoading(true);
            try {
                const res = await axiosInstance.get('/pharmacy/medicines', {
                    params: { search: q, isActive: 'true', limit: 20 },
                });
                setAssignResults(res?.data?.data || []);
            } catch {
                setAssignResults([]);
            } finally {
                setAssignLoading(false);
            }
        }, 500);
        return () => { if (assignTimeoutRef.current) clearTimeout(assignTimeoutRef.current); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assignQuery, selectedBinId]);

    // ---- store actions ----------------------------------------------------

    const saveStore = async () => {
        const name = storeDraft.name.trim();
        if (!name) { toast.warn(t('rackpage.store_name_required', 'Give the counter a name')); return; }

        const payload = {
            name,
            code: storeDraft.code.trim() || null,
            type: storeDraft.type,
            address: storeDraft.address.trim() || null,
            gstin: storeDraft.gstin.trim().toUpperCase() || null,
            drugLicenseNo: storeDraft.drugLicenseNo.trim() || null,
            isDefault: storeDraft.isDefault,
        };

        setMutating(true);
        try {
            if (storeDraft.storeId) {
                await axiosInstance.put(`/pharmacy/stores/${storeDraft.storeId}`, payload);
                toast.success(t('rackpage.store_updated', 'Counter updated'));
            } else {
                await axiosInstance.post('/pharmacy/stores', payload);
                toast.success(t('rackpage.store_created', 'Counter created'));
            }
            setStoreDraft(null);
            refetch();
        } catch (err) {
            toast.error(apiError(err, t('rackpage.store_save_error', 'Failed to save the counter')));
        } finally {
            setMutating(false);
        }
    };

    const makeDefault = async (store) => {
        setMutating(true);
        try {
            await axiosInstance.put(`/pharmacy/stores/${store.storeId}`, { isDefault: true });
            toast.success(t('rackpage.default_set', '{{name}} is now the default counter', { name: store.name }));
            refetch();
        } catch (err) {
            toast.warn(apiError(err, t('rackpage.default_error', 'Failed to change the default counter')));
        } finally {
            setMutating(false);
        }
    };

    /**
     * Close a counter. The server refuses while stock is still on its shelves,
     * and the refusal is the useful half: FEFO allocates by store and never looks
     * at the store's status, so closing does not make that stock unsellable — it
     * makes it invisible in the picker while it stays in the valuation.
     */
    const closeStore = async (store) => {
        const result = await MySwal.fire({
            title: t('rackpage.close_store_title', 'Close this counter?'),
            text: t('rackpage.close_store_text', 'It disappears from the pharmacy screens. Its bills, batches and ledger stay exactly as they are.'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#e5e7eb',
            confirmButtonText: t('rackpage.close', 'Close counter'),
            cancelButtonText: t('rackpage.cancel', 'Cancel'),
        });
        if (!result.isConfirmed) return;

        setMutating(true);
        try {
            const res = await axiosInstance.delete(`/pharmacy/stores/${store.storeId}`);
            toast.success(res?.data?.message || t('rackpage.store_closed', 'Counter closed'));
            refetch();
        } catch (err) {
            toast.warn(apiError(err, t('rackpage.close_store_error', 'Failed to close the counter')));
        } finally {
            setMutating(false);
        }
    };

    // ---- bin actions ------------------------------------------------------

    const saveBin = async () => {
        const code = binDraft.code.trim();
        if (!code) { toast.warn(t('rackpage.bin_code_required', 'The bin needs the label that is physically on it')); return; }

        const payload = {
            code,
            rackName: binDraft.rackName.trim() || null,
            rowLabel: binDraft.rowLabel.trim() || null,
            colLabel: binDraft.colLabel.trim() || null,
            bin: binDraft.bin.trim() || null,
            type: binDraft.type,
            pickSequence: Number(binDraft.pickSequence) || 0,
        };

        setMutating(true);
        try {
            if (binDraft.locationId) {
                await axiosInstance.put(`/pharmacy/locations/${binDraft.locationId}`, payload);
                toast.success(t('rackpage.bin_updated', 'Bin updated'));
            } else {
                await axiosInstance.post('/pharmacy/locations', { ...payload, storeId });
                toast.success(t('rackpage.bin_created', 'Bin created'));
            }
            setBinDraft(null);
            refetch();
        } catch (err) {
            toast.error(apiError(err, t('rackpage.bin_save_error', 'Failed to save the bin')));
        } finally {
            setMutating(false);
        }
    };

    /**
     * Retire a bin. Refused while anything still points at it, and that is not
     * tidiness: a retired rack that is still a medicine's home bin prints "A-12"
     * on tomorrow's pick list and sends a pharmacist to furniture that no longer
     * exists. The server sends back how many medicines and batches are in the
     * way, so the message says what to reassign.
     */
    const retireBin = async (bin) => {
        const result = await MySwal.fire({
            title: t('rackpage.retire_title', 'Retire bin {{code}}?', { code: bin.code }),
            text: t('rackpage.retire_text', 'It stops appearing on pick lists. Dispensed lines that were picked from it keep the code they recorded.'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#e5e7eb',
            confirmButtonText: t('rackpage.retire', 'Retire'),
            cancelButtonText: t('rackpage.cancel', 'Cancel'),
        });
        if (!result.isConfirmed) return;

        setMutating(true);
        try {
            await axiosInstance.delete(`/pharmacy/locations/${bin.locationId}`);
            toast.success(t('rackpage.bin_retired', 'Bin retired'));
            if (selectedBinId === bin.locationId) setSelectedBinId(null);
            refetch();
        } catch (err) {
            toast.warn(apiError(err, t('rackpage.retire_error', 'Failed to retire the bin')));
        } finally {
            setMutating(false);
        }
    };

    // Assigning a home bin is an edit to the DRUG MASTER, which is why it goes
    // through /medicines and needs pharmacy.stock.manage rather than the
    // location permission that governs the rest of this screen.
    const assignMedicine = async (medicine) => {
        setMutating(true);
        try {
            await axiosInstance.put(`/pharmacy/medicines/${medicine.medicineId}`, { locationId: selectedBinId });
            toast.success(t('rackpage.assigned', '{{name}} now lives in {{code}}', {
                name: medicine.name, code: selectedBin?.code || '',
            }));
            setAssignQuery('');
            setAssignResults([]);
            refetch();
        } catch (err) {
            toast.warn(apiError(err, t('rackpage.assign_error', 'Failed to assign the medicine')));
        } finally {
            setMutating(false);
        }
    };

    const unassignMedicine = async (medicine) => {
        setMutating(true);
        try {
            await axiosInstance.put(`/pharmacy/medicines/${medicine.medicineId}`, { locationId: null });
            toast.success(t('rackpage.unassigned', '{{name}} has no home bin now', { name: medicine.name }));
            refetch();
        } catch (err) {
            toast.warn(apiError(err, t('rackpage.unassign_error', 'Failed to remove the medicine from this bin')));
        } finally {
            setMutating(false);
        }
    };

    // ---- small renderers --------------------------------------------------

    const typeOf = (type) => BIN_TYPES[type] || BIN_TYPES.shelf;

    const TypeChip = ({ type, className = '' }) => {
        const cfg = typeOf(type);
        const Icon = cfg.icon;
        return (
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[11px] font-semibold ${cfg.chip} ${className}`}>
                <Icon className="w-3 h-3" />
                {t(cfg.labelKey, cfg.label)}
            </span>
        );
    };

    const activeStores = stores.filter((s) => s.status === 'active');
    const closedStores = stores.filter((s) => s.status !== 'active');
    const selectedStore = stores.find((s) => s.storeId === storeId) || null;
    const inputCls = 'w-full px-3 py-2.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]';
    const labelCls = 'text-xs font-medium text-slate-500';

    return (
        <div data-testid="rack-locations-page-container" className="bg-white p-4 rounded-2xl shadow-md sm:p-6 w-full">
            <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                <div className="ml-2">
                    <h1 data-testid="rack-locations-page-title" className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">
                        {t('rackpage.title', 'Counters & rack bins')}
                    </h1>
                    <p className="text-xs text-slate-400 mt-1">
                        {t('rackpage.subtitle', 'The pharmacy as a room — where each counter is, and what sits on which shelf.')}
                    </p>
                </div>
                <button
                    data-testid="add-store-button"
                    onClick={() => setStoreDraft({ ...EMPTY_STORE, isDefault: stores.length === 0 })}
                    disabled={!canManageStock}
                    title={canManageStock ? undefined : t('rackpage.no_store_permission', 'You do not have permission to manage pharmacy counters')}
                    className={`group flex items-center gap-2 w-full sm:w-auto px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg transition-all duration-300 font-semibold text-sm whitespace-nowrap ${canManageStock ? 'cursor-pointer hover:shadow-xl hover:scale-105' : 'opacity-50 cursor-not-allowed'}`}
                >
                    <Plus className="w-5 h-5" />
                    {t('rackpage.add_store', 'Add counter')}
                </button>
            </div>

            {/* ---- the counters ---- */}
            {storesLoading ? (
                <div className="flex flex-col items-center justify-center py-4 pt-8">
                    <CommonLoader />
                    <span className="text-xl text-[#4ABDBD]">{t('rackpage.loading', 'Loading...')}</span>
                </div>
            ) : (
                <div data-testid="stores-strip" className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 mb-8">
                    {activeStores.map((s) => {
                        const isSelected = s.storeId === storeId;
                        return (
                            <div
                                key={s.storeId}
                                data-testid={`store-card-${s.storeId}`}
                                onClick={() => setStoreId(s.storeId)}
                                className={`text-left p-4 rounded-2xl border transition-all duration-300 cursor-pointer ${isSelected
                                    ? 'border-[#4ABDBD] bg-gradient-to-br from-[#4ABDBD]/10 to-cyan-50 shadow-md'
                                    : 'border-gray-200 bg-white hover:border-[#4ABDBD]/60 hover:shadow'}`}
                            >
                                <div className="flex items-start justify-between gap-2">
                                    <div className="flex items-center gap-2 min-w-0">
                                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-[#4ABDBD] text-white' : 'bg-slate-100 text-slate-500'}`}>
                                            <StoreIcon className="w-4 h-4" />
                                        </div>
                                        <div className="min-w-0">
                                            <div className="font-semibold text-gray-800 text-sm truncate">{s.name}</div>
                                            <div className="text-[11px] text-slate-400 truncate">
                                                {[s.code, t(STORE_TYPES.find((x) => x.value === s.type)?.labelKey || 'rackpage.store_op', STORE_TYPES.find((x) => x.value === s.type)?.label || s.type)].filter(Boolean).join(' · ')}
                                            </div>
                                        </div>
                                    </div>
                                    {s.isDefault && (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#D7A05C]/15 text-[#8a6127] border border-[#D7A05C]/50 text-[10px] font-bold flex-shrink-0">
                                            <Star className="w-3 h-3" />
                                            {t('rackpage.default', 'Default')}
                                        </span>
                                    )}
                                </div>

                                <div className="grid grid-cols-3 gap-2 mt-3 text-center">
                                    <div>
                                        <div className="text-sm font-bold text-gray-700">{s.batchCount ?? 0}</div>
                                        <div className="text-[10px] text-slate-400 uppercase tracking-wide">{t('rackpage.batches', 'Batches')}</div>
                                    </div>
                                    <div>
                                        <div className="text-sm font-bold text-gray-700">{fmtQty(s.qtyOnHand)}</div>
                                        <div className="text-[10px] text-slate-400 uppercase tracking-wide">{t('rackpage.units', 'Units')}</div>
                                    </div>
                                    <div>
                                        {/* Valued at MRP, which is the number a manager
                                            asks for. Purchase-rate valuation belongs to
                                            the purchase module. */}
                                        <div className="text-sm font-bold text-gray-700">{fmtQty(s.stockValue)}</div>
                                        <div className="text-[10px] text-slate-400 uppercase tracking-wide">{t('rackpage.value_mrp', 'Value @MRP')}</div>
                                    </div>
                                </div>

                                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
                                    <button
                                        data-testid={`edit-store-${s.storeId}`}
                                        onClick={(e) => { e.stopPropagation(); setStoreDraft({ ...EMPTY_STORE, ...s, address: s.address || '', gstin: s.gstin || '', drugLicenseNo: s.drugLicenseNo || '', code: s.code || '' }); }}
                                        disabled={!canManageStock}
                                        title={t('rackpage.edit', 'Edit')}
                                        className={`p-1.5 rounded-lg text-[#4ABDBD] bg-blue-50 ${canManageStock ? 'hover:bg-[#4ABDBD] hover:text-white cursor-pointer' : 'opacity-40 cursor-not-allowed'}`}
                                    >
                                        <Pencil className="w-3.5 h-3.5" />
                                    </button>
                                    {!s.isDefault && (
                                        <button
                                            data-testid={`default-store-${s.storeId}`}
                                            onClick={(e) => { e.stopPropagation(); makeDefault(s); }}
                                            disabled={!canManageStock || mutating}
                                            title={t('rackpage.make_default', 'Make this the default counter')}
                                            className={`p-1.5 rounded-lg text-[#8a6127] bg-[#D7A05C]/15 ${canManageStock && !mutating ? 'hover:bg-[#D7A05C] hover:text-white cursor-pointer' : 'opacity-40 cursor-not-allowed'}`}
                                        >
                                            <Star className="w-3.5 h-3.5" />
                                        </button>
                                    )}
                                    <button
                                        data-testid={`close-store-${s.storeId}`}
                                        onClick={(e) => { e.stopPropagation(); closeStore(s); }}
                                        disabled={!canManageStock || mutating}
                                        title={t('rackpage.close', 'Close counter')}
                                        className={`p-1.5 rounded-lg text-red-500 bg-red-50 ml-auto ${canManageStock && !mutating ? 'hover:bg-red-500 hover:text-white cursor-pointer' : 'opacity-40 cursor-not-allowed'}`}
                                    >
                                        <Ban className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            </div>
                        );
                    })}

                    {activeStores.length === 0 && (
                        <div className="sm:col-span-2 xl:col-span-3 p-6 text-center text-gray-500 text-sm rounded-2xl border border-dashed border-gray-300">
                            {t('rackpage.no_stores', 'No counter yet. A pharmacy needs at least one — it is what stock, bills and bins all belong to.')}
                        </div>
                    )}

                    {/* Closed counters are kept visible and inert. They still own
                        years of invoices, and hiding them entirely is how somebody
                        creates a duplicate of one that already exists. */}
                    {closedStores.map((s) => (
                        <div key={s.storeId} data-testid={`store-card-${s.storeId}`} className="p-4 rounded-2xl border border-dashed border-gray-300 bg-gray-50 opacity-70">
                            <div className="flex items-center gap-2">
                                <StoreIcon className="w-4 h-4 text-slate-400" />
                                <div className="font-semibold text-slate-500 text-sm">{s.name}</div>
                                <span className="ml-auto text-[10px] font-bold uppercase text-slate-400">{t('rackpage.closed', 'Closed')}</span>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* ---- the room ---- */}
            {storeId && (
                <>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                        <div>
                            <h2 className="text-lg font-bold text-[#1a365d]">
                                {t('rackpage.rack_map', 'Rack map')}
                                <span className="text-sm font-normal text-slate-400 ml-2">
                                    {selectedStore?.name} · {t('rackpage.bin_count', '{{n}} bins', { n: totalBins })}
                                </span>
                            </h2>
                            <p className="text-[11px] text-slate-400 mt-0.5 flex items-center gap-1">
                                <ArrowDownUp className="w-3 h-3" />
                                {t('rackpage.pick_order_hint', 'Bins are shown in pick order — the walk a pharmacist actually does, not alphabetical.')}
                            </p>
                        </div>
                        <button
                            data-testid="add-bin-button"
                            onClick={() => setBinDraft({ ...EMPTY_BIN })}
                            disabled={!canManageLocations}
                            title={canManageLocations ? undefined : t('rackpage.no_bin_permission', 'You do not have permission to manage rack bins')}
                            className={`flex items-center gap-2 px-4 py-2 rounded-xl border border-[#4ABDBD] text-[#2D8A8A] font-semibold text-sm whitespace-nowrap ${canManageLocations ? 'hover:bg-[#4ABDBD]/10 cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}
                        >
                            <Plus className="w-4 h-4" />
                            {t('rackpage.add_bin', 'Add bin')}
                        </button>
                    </div>

                    {/* The legend is not decoration either — it is where somebody
                        laying out a new rack reads what each kind of bin commits
                        them to. */}
                    <div data-testid="bin-type-legend" className="flex flex-wrap gap-2 mb-5">
                        {BIN_TYPE_KEYS.map((key) => {
                            const cfg = BIN_TYPES[key];
                            const Icon = cfg.icon;
                            return (
                                <span
                                    key={key}
                                    title={cfg.note ? t(cfg.noteKey, cfg.note) : undefined}
                                    className={`inline-flex items-center gap-1 px-2 py-1 rounded-md border text-[11px] font-semibold ${cfg.chip}`}
                                >
                                    <Icon className="w-3 h-3" />
                                    {t(cfg.labelKey, cfg.label)}
                                </span>
                            );
                        })}
                    </div>

                    {mapLoading ? (
                        <div className="flex flex-col items-center justify-center py-4 pt-8">
                            <CommonLoader />
                            <span className="text-xl text-[#4ABDBD]">{t('rackpage.loading', 'Loading...')}</span>
                        </div>
                    ) : racks.length === 0 ? (
                        <div className="p-8 text-center rounded-2xl border border-dashed border-gray-300">
                            <MapPin className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                            <p className="text-sm text-gray-500">
                                {t('rackpage.no_bins', 'No bins mapped at this counter yet. Until there are, every pick list says "no bin" and the pharmacist searches by memory.')}
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-5">
                            {racks.map((rack) => (
                                <div
                                    key={rack.rackName || '_unracked'}
                                    data-testid={`rack-group-${rack.rackName || 'unracked'}`}
                                    className="rounded-2xl border border-gray-200 overflow-hidden"
                                >
                                    <div className="flex flex-wrap items-center gap-3 px-4 py-2.5 bg-gradient-to-r from-slate-50 to-blue-50/40 border-b border-gray-200">
                                        <span className="font-bold text-sm text-[#2D8A8A]">
                                            {rack.rackName || t('rackpage.unracked', 'Unnamed rack')}
                                        </span>
                                        {!rack.rackName && (
                                            // An unmapped bin is exactly the one somebody
                                            // needs to go and label, so it is named rather
                                            // than quietly dropped.
                                            <span className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 font-semibold">
                                                {t('rackpage.needs_label', 'needs a rack name')}
                                            </span>
                                        )}
                                        <span className="text-[11px] text-slate-500">
                                            {t('rackpage.rack_summary', '{{bins}} bins · {{meds}} medicines · {{qty}} units', {
                                                bins: (rack.bins || []).length,
                                                meds: rack.medicineCount || 0,
                                                qty: fmtQty(rack.qtyOnHand),
                                            })}
                                        </span>
                                    </div>

                                    <div className="p-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                                        {(rack.bins || []).map((bin) => {
                                            const cfg = typeOf(bin.type);
                                            const Icon = cfg.icon;
                                            const retired = bin.status !== 'active';
                                            const isOpen = bin.locationId === selectedBinId;
                                            return (
                                                <div
                                                    key={bin.locationId}
                                                    data-testid={`bin-card-${bin.locationId}`}
                                                    onClick={() => setSelectedBinId(isOpen ? null : bin.locationId)}
                                                    className={`relative p-3 rounded-xl border-2 transition-all duration-200 cursor-pointer ${cfg.card} ${isOpen ? 'ring-2 ring-[#4ABDBD] shadow-md' : ''} ${retired ? 'opacity-50 grayscale' : ''}`}
                                                >
                                                    <div className="flex items-start justify-between gap-2">
                                                        <div className="flex items-center gap-2 min-w-0">
                                                            <Icon className="w-4 h-4 flex-shrink-0 text-slate-500" />
                                                            <span className="text-base font-extrabold text-gray-800 truncate">{bin.code}</span>
                                                        </div>
                                                        <span
                                                            title={t('rackpage.pick_sequence', 'Pick sequence')}
                                                            className="text-[10px] font-bold text-slate-500 bg-white/70 border border-slate-200 rounded px-1.5 py-0.5 flex-shrink-0"
                                                        >
                                                            #{bin.pickSequence ?? 0}
                                                        </span>
                                                    </div>

                                                    <div className="mt-2"><TypeChip type={bin.type} /></div>

                                                    {(bin.rowLabel || bin.colLabel || bin.bin) && (
                                                        <div className="text-[11px] text-slate-500 mt-1.5">
                                                            {[bin.rowLabel && `${t('rackpage.row', 'Row')} ${bin.rowLabel}`,
                                                            bin.colLabel && `${t('rackpage.col', 'Col')} ${bin.colLabel}`,
                                                            bin.bin && `${t('rackpage.bin', 'Bin')} ${bin.bin}`]
                                                                .filter(Boolean).join(' · ')}
                                                        </div>
                                                    )}

                                                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-[11px] text-slate-600">
                                                        <span className="inline-flex items-center gap-1">
                                                            <Pill className="w-3 h-3" />{bin.medicineCount || 0}
                                                        </span>
                                                        <span className="inline-flex items-center gap-1">
                                                            <Boxes className="w-3 h-3" />{bin.batchCount || 0}
                                                        </span>
                                                        <span className="font-semibold">{fmtQty(bin.qtyOnHand)} {t('rackpage.units_short', 'units')}</span>
                                                    </div>

                                                    {bin.nearestExpiry && (
                                                        <div className="text-[11px] text-slate-500 mt-1">
                                                            {t('rackpage.nearest_expiry', 'Soonest expiry {{date}}', { date: fmtDate(bin.nearestExpiry) })}
                                                        </div>
                                                    )}

                                                    {cfg.note && (
                                                        <div className="text-[10px] leading-snug mt-2 pt-2 border-t border-black/5 text-slate-600">
                                                            {t(cfg.noteKey, cfg.note)}
                                                        </div>
                                                    )}

                                                    {retired && (
                                                        <span className="absolute top-2 right-2 text-[10px] font-bold uppercase text-slate-500">
                                                            {t('rackpage.retired', 'Retired')}
                                                        </span>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}

            {/* ---- what is filed in the open bin ---- */}
            {selectedBin && (
                <div data-testid="bin-detail-panel" className="mt-6 rounded-2xl border-2 border-[#4ABDBD]/50 overflow-hidden">
                    <div className="flex flex-wrap items-center gap-3 px-4 py-3 bg-gradient-to-r from-[#4ABDBD]/10 to-cyan-50 border-b border-[#4ABDBD]/30">
                        <span className="text-lg font-extrabold text-gray-800">{selectedBin.code}</span>
                        <TypeChip type={selectedBin.type} />
                        <span className="text-xs text-slate-500">
                            {selectedBin.rackName || t('rackpage.unracked', 'Unnamed rack')} · {t('rackpage.pick_no', 'pick #{{n}}', { n: selectedBin.pickSequence ?? 0 })}
                        </span>
                        <div className="flex items-center gap-2 ml-auto">
                            <button
                                data-testid={`edit-bin-${selectedBin.locationId}`}
                                onClick={() => setBinDraft({
                                    locationId: selectedBin.locationId,
                                    code: selectedBin.code || '',
                                    rackName: selectedBin.rackName || '',
                                    rowLabel: selectedBin.rowLabel || '',
                                    colLabel: selectedBin.colLabel || '',
                                    bin: selectedBin.bin || '',
                                    type: selectedBin.type || 'shelf',
                                    pickSequence: selectedBin.pickSequence ?? 0,
                                })}
                                disabled={!canManageLocations}
                                className={`p-2 rounded-xl text-[#4ABDBD] bg-white border border-[#4ABDBD]/40 ${canManageLocations ? 'hover:bg-[#4ABDBD] hover:text-white cursor-pointer' : 'opacity-40 cursor-not-allowed'}`}
                                title={t('rackpage.edit_bin', 'Edit bin')}
                            >
                                <Pencil className="w-4 h-4" />
                            </button>
                            <button
                                data-testid={`retire-bin-${selectedBin.locationId}`}
                                onClick={() => retireBin(selectedBin)}
                                disabled={!canManageLocations || mutating}
                                className={`p-2 rounded-xl text-red-500 bg-white border border-red-200 ${canManageLocations && !mutating ? 'hover:bg-red-500 hover:text-white cursor-pointer' : 'opacity-40 cursor-not-allowed'}`}
                                title={t('rackpage.retire_bin', 'Retire bin')}
                            >
                                <Ban className="w-4 h-4" />
                            </button>
                            <button
                                onClick={() => setSelectedBinId(null)}
                                className="p-2 rounded-xl text-slate-400 hover:text-slate-700 cursor-pointer"
                                title={t('rackpage.close_panel', 'Close')}
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    </div>

                    {typeOf(selectedBin.type).note && (
                        <div className={`px-4 py-2 text-[11px] font-semibold border-b ${typeOf(selectedBin.type).chip}`}>
                            <AlertTriangle className="w-3 h-3 inline mr-1 -mt-0.5" />
                            {t(typeOf(selectedBin.type).noteKey, typeOf(selectedBin.type).note)}
                        </div>
                    )}

                    <div className="p-4 grid grid-cols-1 lg:grid-cols-2 gap-5">
                        <div>
                            <h3 className="text-sm font-bold text-[#2D8A8A] mb-2">
                                {t('rackpage.lives_here', 'Medicines that call this bin home')}
                            </h3>
                            {binMedicinesLoading ? (
                                <div className="flex items-center gap-2 text-sm text-slate-400 py-4">
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    {t('rackpage.loading', 'Loading...')}
                                </div>
                            ) : binMedicines.length === 0 ? (
                                <p className="text-sm text-slate-400 py-4">
                                    {t('rackpage.bin_empty', 'Nothing is filed here yet. A bin with no medicine assigned never appears on a pick list.')}
                                </p>
                            ) : (
                                <ul data-testid="bin-medicine-list" className="space-y-2 max-h-72 overflow-y-auto pr-1">
                                    {binMedicines.map((m) => (
                                        <li
                                            key={m.medicineId}
                                            data-testid={`bin-medicine-${m.medicineId}`}
                                            className="flex items-center gap-2 p-2 rounded-xl border border-gray-200 bg-white"
                                        >
                                            <Pill className="w-4 h-4 text-[#2D8A8A] flex-shrink-0" />
                                            <div className="min-w-0 flex-1">
                                                <div className="text-sm font-semibold text-gray-700 truncate">{m.name}</div>
                                                <div className="text-[11px] text-slate-400 truncate">
                                                    {[m.genericName, m.strength, m.brand].filter(Boolean).join(' · ')}
                                                </div>
                                            </div>
                                            {m.schedule && m.schedule !== 'none' && (
                                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold border flex-shrink-0 ${m.schedule === 'H' ? 'bg-amber-100 text-amber-800 border-amber-300' : 'bg-red-100 text-red-700 border-red-400'}`}>
                                                    {m.schedule}
                                                </span>
                                            )}
                                            <button
                                                data-testid={`unassign-medicine-${m.medicineId}`}
                                                onClick={() => unassignMedicine(m)}
                                                disabled={!canManageStock || mutating}
                                                title={t('rackpage.unassign', 'Remove from this bin')}
                                                className={`p-1.5 rounded-lg text-red-500 bg-red-50 flex-shrink-0 ${canManageStock && !mutating ? 'hover:bg-red-500 hover:text-white cursor-pointer' : 'opacity-40 cursor-not-allowed'}`}
                                            >
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        <div>
                            <h3 className="text-sm font-bold text-[#2D8A8A] mb-2 flex items-center gap-2">
                                <PackagePlus className="w-4 h-4" />
                                {t('rackpage.assign_here', 'File a medicine here')}
                            </h3>
                            <div className="relative">
                                <input
                                    data-testid="bin-assign-search"
                                    value={assignQuery}
                                    onChange={(e) => setAssignQuery(e.target.value.replace(/[<>]/g, '').slice(0, 60))}
                                    placeholder={t('rackpage.assign_placeholder', 'Search the catalogue…')}
                                    disabled={!canManageStock}
                                    className={`${inputCls} pl-10 ${canManageStock ? '' : 'bg-gray-50 cursor-not-allowed'}`}
                                />
                                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#4ABDBD]" />
                            </div>
                            {!canManageStock && (
                                <p className="text-[11px] text-slate-400 mt-1">
                                    {t('rackpage.assign_needs_stock_permission', 'A home bin is part of the drug master, so changing it needs the stock permission rather than the rack one.')}
                                </p>
                            )}

                            {assignLoading && (
                                <div className="flex items-center gap-2 text-sm text-slate-400 py-3">
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    {t('rackpage.searching', 'Searching...')}
                                </div>
                            )}

                            {!assignLoading && assignResults.length > 0 && (
                                <ul data-testid="bin-assign-results" className="mt-2 space-y-2 max-h-64 overflow-y-auto pr-1">
                                    {assignResults.map((m) => {
                                        const alreadyHere = m.locationId === selectedBin.locationId;
                                        return (
                                            <li key={m.medicineId} className="flex items-center gap-2 p-2 rounded-xl border border-gray-200 bg-white">
                                                <div className="min-w-0 flex-1">
                                                    <div className="text-sm font-semibold text-gray-700 truncate">{m.name}</div>
                                                    <div className="text-[11px] text-slate-400 truncate">
                                                        {[m.genericName, m.strength].filter(Boolean).join(' · ')}
                                                    </div>
                                                </div>
                                                {/* Moving a medicine that already has a home
                                                    bin is legitimate — shelves get rearranged —
                                                    but it should never be a surprise, so the
                                                    button says which one it is leaving. */}
                                                {alreadyHere ? (
                                                    <span className="text-[11px] text-slate-400 flex-shrink-0">{t('rackpage.already_here', 'Already here')}</span>
                                                ) : (
                                                    <button
                                                        data-testid={`assign-medicine-${m.medicineId}`}
                                                        onClick={() => assignMedicine(m)}
                                                        disabled={mutating}
                                                        className="px-2.5 py-1 rounded-lg text-[11px] font-semibold text-white bg-gradient-to-r from-[#4ABDBD] to-[#2D8A8A] hover:shadow disabled:opacity-50 flex-shrink-0 cursor-pointer"
                                                    >
                                                        {m.locationId ? t('rackpage.move_here', 'Move here') : t('rackpage.file_here', 'File here')}
                                                    </button>
                                                )}
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}

                            {!assignLoading && assignQuery.trim().length >= 2 && assignResults.length === 0 && (
                                <p className="text-sm text-slate-400 py-3">{t('rackpage.no_matches', 'Nothing in the catalogue matches that.')}</p>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ---- counter form ---- */}
            {storeDraft && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4" data-testid="store-form-modal">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                            <h2 className="text-lg font-bold text-[#1a365d]">
                                {storeDraft.storeId ? t('rackpage.edit_store', 'Edit counter') : t('rackpage.add_store', 'Add counter')}
                            </h2>
                            <button type="button" onClick={() => setStoreDraft(null)} data-testid="store-form-close" className="text-gray-400 hover:text-gray-600">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="sm:col-span-2">
                                <label className={labelCls}>{t('rackpage.store_name', 'Name')} *</label>
                                <input data-testid="store-form-name" value={storeDraft.name}
                                    onChange={(e) => setStoreDraft((d) => ({ ...d, name: e.target.value }))}
                                    maxLength={80} className={inputCls} />
                            </div>
                            <div>
                                <label className={labelCls}>{t('rackpage.store_code', 'Short code')}</label>
                                <input data-testid="store-form-code" value={storeDraft.code}
                                    onChange={(e) => setStoreDraft((d) => ({ ...d, code: e.target.value }))}
                                    maxLength={20} className={inputCls} />
                            </div>
                            <div>
                                <label className={labelCls}>{t('rackpage.store_type', 'Type')}</label>
                                <select data-testid="store-form-type" value={storeDraft.type}
                                    onChange={(e) => setStoreDraft((d) => ({ ...d, type: e.target.value }))}
                                    className={`tt-select ${inputCls}`}>
                                    {STORE_TYPES.map((s) => (
                                        <option key={s.value} value={s.value}>{t(s.labelKey, s.label)}</option>
                                    ))}
                                </select>
                                {/* Not cosmetic: inpatient supply is a composite
                                    healthcare service and bills GST-exempt, so the
                                    counter a sale belongs to changes the tax on it. */}
                                <p className="text-[11px] text-slate-400 mt-1">
                                    {t('rackpage.store_type_hint', 'Inpatient supply bills GST-exempt — the counter decides which rules a sale follows.')}
                                </p>
                            </div>
                            <div className="sm:col-span-2">
                                <label className={labelCls}>{t('rackpage.store_address', 'Address on the bill')}</label>
                                <textarea data-testid="store-form-address" value={storeDraft.address}
                                    onChange={(e) => setStoreDraft((d) => ({ ...d, address: e.target.value }))}
                                    rows={2} maxLength={300} className={`${inputCls} resize-none`} />
                            </div>
                            <div>
                                <label className={labelCls}>{t('rackpage.gstin', 'GSTIN')}</label>
                                <input data-testid="store-form-gstin" value={storeDraft.gstin}
                                    onChange={(e) => setStoreDraft((d) => ({ ...d, gstin: e.target.value.toUpperCase().slice(0, 15) }))}
                                    maxLength={15} className={`${inputCls} font-mono`} />
                            </div>
                            <div>
                                <label className={labelCls}>{t('rackpage.drug_licence', 'Drug licence no.')}</label>
                                <input data-testid="store-form-licence" value={storeDraft.drugLicenseNo}
                                    onChange={(e) => setStoreDraft((d) => ({ ...d, drugLicenseNo: e.target.value }))}
                                    maxLength={40} className={inputCls} />
                                {/* Both print on every bill this counter raises and
                                    have to keep printing the values that were valid
                                    on the bill's date, years after it shuts. */}
                                <p className="text-[11px] text-slate-400 mt-1">
                                    {t('rackpage.licence_hint', 'Printed on every bill this counter raises.')}
                                </p>
                            </div>
                            {/* A hospital always has exactly one default counter —
                                there is no "no default" state, because a screen that
                                opens on nothing makes every dispense start with an
                                unnecessary choice, and an unset picker is how somebody
                                decrements the wrong counter's stock. So the box can be
                                ticked but not unticked: the way to move the default is
                                to give it to another counter. */}
                            <div className="sm:col-span-2 flex items-start gap-2">
                                <input id="store-default" data-testid="store-form-default" type="checkbox"
                                    checked={storeDraft.isDefault}
                                    disabled={Boolean(storeDraft.storeId) && storeDraft.isDefault}
                                    onChange={(e) => setStoreDraft((d) => ({ ...d, isDefault: e.target.checked }))}
                                    className="w-4 h-4 mt-0.5 accent-[#4ABDBD] disabled:opacity-50" />
                                <label htmlFor="store-default" className="text-sm text-gray-600">
                                    {t('rackpage.make_default_label', 'Open the pharmacy screens on this counter')}
                                    {Boolean(storeDraft.storeId) && storeDraft.isDefault && (
                                        <span className="block text-[11px] text-slate-400">
                                            {t('rackpage.default_move_hint', 'Already the default. Make another counter the default to move it.')}
                                        </span>
                                    )}
                                </label>
                            </div>
                        </div>

                        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-100">
                            <button type="button" onClick={() => setStoreDraft(null)}
                                className="px-4 py-2 text-sm rounded-xl border border-gray-300 text-gray-600 hover:bg-gray-50 cursor-pointer">
                                {t('rackpage.cancel', 'Cancel')}
                            </button>
                            <button type="button" onClick={saveStore} disabled={mutating} data-testid="store-form-submit"
                                className="px-4 py-2 text-sm rounded-xl text-white font-semibold bg-gradient-to-r from-[#4ABDBD] to-[#2D8A8A] hover:shadow-lg disabled:opacity-60 flex items-center gap-2 cursor-pointer">
                                {mutating && <Loader2 className="w-4 h-4 animate-spin" />}
                                {storeDraft.storeId ? t('rackpage.save', 'Save changes') : t('rackpage.create', 'Create counter')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ---- bin form ---- */}
            {binDraft && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4" data-testid="bin-form-modal">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                            <h2 className="text-lg font-bold text-[#1a365d]">
                                {binDraft.locationId ? t('rackpage.edit_bin', 'Edit bin') : t('rackpage.add_bin', 'Add bin')}
                            </h2>
                            <button type="button" onClick={() => setBinDraft(null)} data-testid="bin-form-close" className="text-gray-400 hover:text-gray-600">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="sm:col-span-2">
                                <label className={labelCls}>{t('rackpage.bin_code', 'Bin code')} *</label>
                                <input data-testid="bin-form-code" value={binDraft.code}
                                    onChange={(e) => setBinDraft((d) => ({ ...d, code: e.target.value }))}
                                    placeholder="A-12" maxLength={30} className={`${inputCls} font-mono`} />
                                {/* Unique per counter by design: the label is what the
                                    pharmacist reads off the shelf, so a duplicate makes
                                    the pick list ambiguous at exactly the moment it
                                    exists to remove doubt. */}
                                <p className="text-[11px] text-slate-400 mt-1">
                                    {t('rackpage.bin_code_hint', 'Exactly what is physically written on the bin. It has to be unique at this counter.')}
                                </p>
                            </div>
                            <div>
                                <label className={labelCls}>{t('rackpage.rack_name', 'Rack')}</label>
                                <input data-testid="bin-form-rack" value={binDraft.rackName}
                                    onChange={(e) => setBinDraft((d) => ({ ...d, rackName: e.target.value }))}
                                    placeholder={t('rackpage.rack_placeholder', 'e.g. Rack A')} maxLength={40} className={inputCls} />
                            </div>
                            <div>
                                <label className={labelCls}>{t('rackpage.bin_type', 'Type')}</label>
                                <select data-testid="bin-form-type" value={binDraft.type}
                                    onChange={(e) => setBinDraft((d) => ({ ...d, type: e.target.value }))}
                                    className={`tt-select ${inputCls}`}>
                                    {BIN_TYPE_KEYS.map((key) => (
                                        <option key={key} value={key}>{t(BIN_TYPES[key].labelKey, BIN_TYPES[key].label)}</option>
                                    ))}
                                </select>
                                {BIN_TYPES[binDraft.type]?.note && (
                                    <p className="text-[11px] text-slate-500 mt-1 flex items-start gap-1">
                                        <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0 text-amber-500" />
                                        {t(BIN_TYPES[binDraft.type].noteKey, BIN_TYPES[binDraft.type].note)}
                                    </p>
                                )}
                            </div>
                            <div className="grid grid-cols-3 gap-2 sm:col-span-2">
                                <div>
                                    <label className={labelCls}>{t('rackpage.row', 'Row')}</label>
                                    <input data-testid="bin-form-row" value={binDraft.rowLabel}
                                        onChange={(e) => setBinDraft((d) => ({ ...d, rowLabel: e.target.value }))}
                                        maxLength={10} className={inputCls} />
                                </div>
                                <div>
                                    <label className={labelCls}>{t('rackpage.col', 'Column')}</label>
                                    <input data-testid="bin-form-col" value={binDraft.colLabel}
                                        onChange={(e) => setBinDraft((d) => ({ ...d, colLabel: e.target.value }))}
                                        maxLength={10} className={inputCls} />
                                </div>
                                <div>
                                    <label className={labelCls}>{t('rackpage.bin', 'Bin')}</label>
                                    <input data-testid="bin-form-bin" value={binDraft.bin}
                                        onChange={(e) => setBinDraft((d) => ({ ...d, bin: e.target.value }))}
                                        maxLength={10} className={inputCls} />
                                </div>
                            </div>
                            <div className="sm:col-span-2">
                                <label className={labelCls}>{t('rackpage.pick_sequence', 'Pick sequence')}</label>
                                <input data-testid="bin-form-sequence" type="number" min="0" step="1"
                                    value={binDraft.pickSequence}
                                    onChange={(e) => setBinDraft((d) => ({ ...d, pickSequence: e.target.value }))}
                                    className={inputCls} />
                                <p className="text-[11px] text-slate-400 mt-1">
                                    {t('rackpage.sequence_hint', 'The order this bin comes in on the walk. Low numbers first; leave everything at 0 and bins fall back to code order.')}
                                </p>
                            </div>
                            {binDraft.locationId && (
                                <p className="sm:col-span-2 text-[11px] text-slate-400">
                                    {/* Moving a bin between counters would strand every
                                        medicine and batch pointing at it in a room they
                                        are not physically in, so the server refuses it. */}
                                    {t('rackpage.bin_store_fixed', 'A bin cannot be moved to another counter — retire it and create one there.')}
                                </p>
                            )}
                        </div>

                        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-100">
                            <button type="button" onClick={() => setBinDraft(null)}
                                className="px-4 py-2 text-sm rounded-xl border border-gray-300 text-gray-600 hover:bg-gray-50 cursor-pointer">
                                {t('rackpage.cancel', 'Cancel')}
                            </button>
                            <button type="button" onClick={saveBin} disabled={mutating} data-testid="bin-form-submit"
                                className="px-4 py-2 text-sm rounded-xl text-white font-semibold bg-gradient-to-r from-[#4ABDBD] to-[#2D8A8A] hover:shadow-lg disabled:opacity-60 flex items-center gap-2 cursor-pointer">
                                {mutating && <Loader2 className="w-4 h-4 animate-spin" />}
                                {binDraft.locationId ? t('rackpage.save', 'Save changes') : t('rackpage.create_bin', 'Create bin')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
